'use client';
import { jsx } from '@emotion/react/jsx-runtime';
import { forwardRef, useState } from 'react';
import { Box } from '@s7/base';
import { useProps } from '@s7/styles';
import { isString } from '@s7/utils';
import { IDENT, defaultProps } from './Image.contract.js';
import useStyles from './Image.styles.js';
import { useImageContext } from './ImageProvider.js';
import { ImageWithPreview } from './ImageWithPreview.js';
import { useResponsiveImage } from './useResponsiveImage.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var Image = /*#__PURE__*/ forwardRef(function(props, ref) {
    var _useProps = useProps(IDENT, defaultProps, props), _useProps_ = _useProps[0], _preview = _useProps_.preview, _fallback = _useProps_.fallback, width = _useProps_.width, height = _useProps_.height, loader = _useProps_.loader, baseUrl = _useProps_.baseUrl, responsive = _useProps_.responsive, lazy = _useProps_.lazy, fit = _useProps_.fit, src = _useProps_.src, srcPrefix = _useProps_.srcPrefix, children = _useProps_.children, className = _useProps_.className, onError = _useProps_.onError, sxs = _useProps[1], others = _object_without_properties_loose(_useProps[0], [
        "preview",
        "fallback",
        "width",
        "height",
        "loader",
        "baseUrl",
        "responsive",
        "lazy",
        "fit",
        "src",
        "srcPrefix",
        "children",
        "className",
        "onError"
    ]);
    var _useState = useState(!src), isError = _useState[0], setIsError = _useState[1];
    var _useStyles = useStyles({
        width: width,
        height: height,
        fit: fit
    }, sxs, IDENT), classes = _useStyles[0], cx = _useStyles[1];
    var context = useImageContext() || {};
    var _ref = {
        baseUrl: baseUrl != null ? baseUrl : context.baseUrl,
        srcPrefix: srcPrefix != null ? srcPrefix : context.srcPrefix,
        loader: loader != null ? loader : context.loader,
        fallback: _fallback != null ? _fallback : context.fallback,
        preview: _preview != null ? _preview : context.preview
    }, fallback = _ref.fallback, preview = _ref.preview, consolidatedParams = _object_without_properties_loose(_ref, [
        "fallback",
        "preview"
    ]);
    var responsiveProps = useResponsiveImage(_extends({
        width: Number(width),
        height: Number(height),
        src: src
    }, consolidatedParams), responsive);
    var handleError = function(event) {
        onError == null ? void 0 : onError(event);
        if (event.defaultPrevented) {
            return;
        }
        setIsError(true);
    };
    if (isError) {
        if (fallback === false) {
            return /*#__PURE__*/ jsx(Box, {
                className: cx(classes.root, classes.fallback, className),
                "data-fallback": "placeholder",
                sx: others.sx
            });
        }
        return /*#__PURE__*/ jsx(Box, {
            className: cx(classes.root, classes.fallback, className),
            "data-fallback": true,
            sx: others.sx,
            children: fallback
        });
    }
    return /*#__PURE__*/ jsx(ImageWithPreview, _extends({
        ref: ref
    }, others, responsiveProps, isError && isString(fallback) && {
        src: fallback
    } || {}, {
        className: cx(classes.root, classes.image, className),
        height: height,
        loading: lazy ? "lazy" : undefined,
        preview: preview,
        width: width,
        onError: handleError,
        children: children
    }));
});
var Image$1 = Image;
Image.displayName = IDENT;

export { Image, Image$1 as default };
